import type { DensitySnapshot, InstrumentRef } from "@screener/protocol";
import { CARD_HEIGHT } from "./card-classes";
import { type DensitySort, defaultSortDirection, sortRows } from "./derive";

/**
 * Вид «карточки» §9 (1.21) — группировка уже отобранных строк по инструменту.
 * Лучший представитель карточки фиксирован по ключу сортировки (§9 1.21):
 * по дистанции — ближайшая плотность, по USD — крупнейшая, по времени жизни
 * — старейшая, независимо от направления, в котором отсортирован сам экран
 * (design review, блок B). Направление меняет только порядок карточек.
 */

export interface DensityCard {
  key: string;
  instrument: InstrumentRef;
  rows: DensitySnapshot[];
  /** Объём самой крупной плотности карточки, USD — основание заливки строк (`cardFillFraction`). */
  maxUsd: number;
  /** Карточка закреплена пользователем (§9 1.52, §65). */
  pinned: boolean;
  /** Сколько живых плотностей инструмента скрыто фильтрами; у незакреплённой всегда 0. */
  hiddenCount: number;
  /**
   * `density_id` строк, чья плотность закончилась после заморозки вида (§77
   * п. 4): строка остаётся на месте последним снимком. `buildCards` поле не
   * ставит — оно есть только у замороженных карточек (`applyFreeze`).
   */
  endedIds?: ReadonlySet<string>;
}

/**
 * Закрепления для `buildCards` (§9 1.52, §65): ключи инструментов
 * (`instrumentKey`) со ссылками на них и все живые плотности потока до
 * фильтров — по ним считается число скрытых строк закреплённой карточки.
 */
export interface PinnedCards {
  keys: ReadonlyMap<string, InstrumentRef>;
  allRows: readonly DensitySnapshot[];
}

/**
 * Порядок строк внутри карточки §9 (1.23, ответ трейдера 23.09.2026):
 * «стакан» — сначала аски, затем биды, каждая сторона по цене якоря от
 * большей к меньшей; «по объёму» — все строки по USD от крупнейшей.
 */
export type CardRowOrder = "book" | "volume";

/**
 * Сравнение строк «стаканом» (design.md, решение 2) — первый собственный
 * компаратор `apps/web`, приватный: у `sortRows` нет ключа «цена», и
 * добавлять его в общий перечень сортировок §9 нельзя. Цена приходит
 * строкой и переводится в `Number` только здесь, никуда не сохраняясь —
 * точности хватает для упорядочения, а сравнение как десятичных дробей
 * было бы своей арифметикой без нужды. При равной цене якоря порядок задаёт
 * `density_id`: одна и та же выдача даёт один и тот же порядок между
 * обновлениями.
 */
function compareBookOrder(left: DensitySnapshot, right: DensitySnapshot): number {
  if (left.side !== right.side) {
    return left.side === "ask" ? -1 : 1;
  }
  const priceDiff = Number(right.anchor_price) - Number(left.anchor_price);
  if (priceDiff !== 0) {
    return priceDiff;
  }
  if (left.density_id === right.density_id) {
    return 0;
  }
  return left.density_id < right.density_id ? -1 : 1;
}

/** Ключ инструмента §3.2: биржа, рынок, базовая и котируемая монета. */
export function instrumentKey(instrument: InstrumentRef): string {
  return `${instrument.exchange}|${instrument.market}|${instrument.base}|${instrument.quote}`;
}

/**
 * Группирует строки по инструменту, выбирает представителя каждой карточки
 * и упорядочивает карточки по представителям под сортировкой экрана.
 * Строки внутри карточки идут в порядке `rowOrder` (§9 1.23) — независимо
 * от сортировки экрана и от порядка карточек, который считается раньше, до
 * этой перестановки (design.md, решение 1): «стакан» — аски над бидами,
 * каждая сторона по цене якоря по убыванию (приватный компаратор
 * `compareBookOrder`); «по объёму» — через `sortRows` по USD от крупнейшей.
 *
 * Закреплённые карточки (§9 1.52, §65 п. 2–3) идут выше остальных тремя
 * группами: закреплённые со строками — по представителям под сортировкой
 * экрана; пустые закреплённые (все плотности скрыты фильтрами или их нет) —
 * по базовой монете, при равенстве по ключу инструмента, чтобы порядок не
 * дрожал между вызовами; затем незакреплённые — по представителям. У
 * закреплённой карточки `hiddenCount` — число плотностей инструмента в
 * `pinned.allRows` за вычетом видимых. `pinned` не передан или `null` —
 * закреплений нет.
 */
export function buildCards(
  rows: readonly DensitySnapshot[],
  sort: DensitySort,
  rowOrder: CardRowOrder,
  pinned: PinnedCards | null = null,
): DensityCard[] {
  const byKey = new Map<string, DensityCard>();
  for (const row of rows) {
    const key = instrumentKey(row.instrument);
    const card = byKey.get(key);
    if (card === undefined) {
      byKey.set(key, {
        key,
        instrument: row.instrument,
        rows: [row],
        maxUsd: 0,
        pinned: pinned?.keys.has(key) ?? false,
        hiddenCount: 0,
      });
    } else {
      card.rows.push(row);
    }
  }

  const emptyPinned: DensityCard[] = [];
  if (pinned !== null) {
    const totalByKey = new Map<string, number>();
    for (const row of pinned.allRows) {
      const key = instrumentKey(row.instrument);
      totalByKey.set(key, (totalByKey.get(key) ?? 0) + 1);
    }
    for (const [key, instrument] of pinned.keys) {
      const total = totalByKey.get(key) ?? 0;
      const card = byKey.get(key);
      if (card === undefined) {
        emptyPinned.push({
          key,
          instrument,
          rows: [],
          maxUsd: 0,
          pinned: true,
          hiddenCount: total,
        });
      } else {
        card.hiddenCount = Math.max(0, total - card.rows.length);
      }
    }
    emptyPinned.sort((left, right) => {
      if (left.instrument.base !== right.instrument.base) {
        return left.instrument.base < right.instrument.base ? -1 : 1;
      }
      return left.key < right.key ? -1 : left.key > right.key ? 1 : 0;
    });
  }

  // Представитель карточки — первая строка под своим, не экранным,
  // направлением (`defaultSortDirection`): выбор представителя не зависит
  // от того, в какую сторону сейчас отсортирован экран.
  const representativeDirection = defaultSortDirection(sort.key);
  const cardByRepresentativeId = new Map<string, DensityCard>();
  const representatives: DensitySnapshot[] = [];
  for (const card of byKey.values()) {
    const [best] = sortRows(card.rows, { key: sort.key, direction: representativeDirection });
    if (best === undefined) {
      // Карточка заводится только при появлении строки — пустой не бывает.
      continue;
    }
    representatives.push(best);
    cardByRepresentativeId.set(best.density_id, card);
  }

  // Порядок карточек — порядок их представителей под сортировкой экрана.
  const orderedCards = sortRows(representatives, sort)
    .map((representative) => cardByRepresentativeId.get(representative.density_id))
    .filter((card): card is DensityCard => card !== undefined);

  // Три группы §65 п. 2–3: закреплённые со строками, пустые закреплённые,
  // остальные. Порядок внутри групп (а) и (в) уже задан сортировкой экрана.
  const result = [
    ...orderedCards.filter((card) => card.pinned),
    ...emptyPinned,
    ...orderedCards.filter((card) => !card.pinned),
  ];

  for (const card of result) {
    card.rows =
      rowOrder === "book"
        ? [...card.rows].sort(compareBookOrder)
        : sortRows(card.rows, { key: "usd", direction: "desc" });
    // Нечисловой объём в максимум не входит: его строка и так без заливки.
    card.maxUsd = Math.max(
      0,
      ...card.rows.map((row) => Number(row.density_usd)).filter((usd) => Number.isFinite(usd)),
    );
  }

  return result;
}

/**
 * Доля заливки строки карточки §9 (1.45): линейно от самой крупной плотности
 * этой карточки, она — вся ширина. Постоянная шкала 1.9 (`volumeBarFraction`)
 * остаётся у таблицы. `Number` — только для доли показа, нигде не хранится.
 * Нечисловой объём или неположительный максимум — пустая заливка.
 */
export function cardFillFraction(densityUsd: string, maxUsd: number): number {
  const usd = Number(densityUsd);
  if (!Number.isFinite(usd) || !Number.isFinite(maxUsd) || maxUsd <= 0) {
    return 0;
  }
  return Math.min(1, Math.max(0, usd / maxUsd));
}

/**
 * Минимальная ширина колонки карточек и промежуток между ними, px.
 *
 * Это константы вёрстки, а не настройка §11 — так же как границы шкалы
 * полосы объёма (`BAR_SCALE_MIN_USD`/`BAR_SCALE_MAX_USD` в `derive.ts`):
 * раскладка обязана выглядеть одинаково у всех, а не настраиваться конфигом
 * демона.
 *
 * 270 — замер худшей строки §9 1.45, §88 в браузере (Playwright,
 * подставной демон, change stage9-redesign-cards, Д1а): объём «100.0M»
 * (самая широкая запись `formatUsd`), цена `117250.5` (BTC с шагом 0.5)
 * с отметкой круглой цены 9 px и значком закрепления 11 px, время жизни
 * `99д 23ч`, дистанция `-12.34%`. Значок «без данных» в худшую строку не
 * входит: устаревшая строка с такой ценой сжимает цену многоточием, а
 * объём, время и дистанция остаются целыми. Дорожки сетки `CardContent`
 * (макет, ширины как нижняя граница): время жизни 52, дистанция 48.2,
 * объём 45.3, три промежутка по 6 = 18 (`gap-x-1.5`, было 8 по макету),
 * отступы `CardContent` 2 × 4 = 8; остаток отдан цене, и ей хватает на
 * весь текст 57 px с отметкой и закреплением на карточке 270 px. Прежний
 * зазор 8 и значок устаревания в худшей строке давали нехватку около 16 px
 * и требовали 290 (четыре колонки вместо пяти на 16").
 *
 * На 16" экране (окно 1728, боковая панель развёрнута, область карточек
 * 1448 px) это пять колонок (§58 п. 1). Прежние 330 (четыре колонки,
 * `decisions.md`, 09.10.2026) держали бейдж стороны и полную цену.
 */
export const CARD_COLUMN_MIN_WIDTH_PX = 270;
export const CARD_COLUMN_GAP_PX = 12;

/**
 * Число колонок по ширине области экрана (design, решение 4): на широком
 * мониторе карточек в ряду больше, а не сами они шире. Нечисловая или
 * отрицательная ширина — запасное значение «одна колонка», как при
 * несработавшем `ResizeObserver`.
 */
export function columnCount(widthPx: number): number {
  if (!Number.isFinite(widthPx) || widthPx <= 0) {
    return 1;
  }
  const columns = Math.floor(
    (widthPx + CARD_COLUMN_GAP_PX) / (CARD_COLUMN_MIN_WIDTH_PX + CARD_COLUMN_GAP_PX),
  );
  return Math.max(1, columns);
}

/**
 * Высота карточки экрана в px по числу её строк (DEN-104, design
 * stage9-cards-layout-no-measure Д1): шапка, отступы тела и строки с зазорами
 * между соседними, у карточки без строк — подпись. `remPx` — корневой шрифт.
 */
export function cardHeightPx(rowCount: number, remPx: number): number {
  const frame = (CARD_HEIGHT.headerRem + 2 * CARD_HEIGHT.bodyPaddingRem) * remPx;
  if (rowCount <= 0) {
    return frame + CARD_HEIGHT.emptyLineRem * remPx + 2 * CARD_HEIGHT.emptyPaddingPx;
  }
  return frame + rowCount * CARD_HEIGHT.rowPx + (rowCount - 1) * CARD_HEIGHT.rowGapRem * remPx;
}

/**
 * Смещение верха строки `rowIndex` от верха карточки, px: шапка, отступ тела и
 * строки выше с зазорами — те же константы, что у `cardHeightPx`.
 */
export function cardRowOffsetPx(rowIndex: number, remPx: number): number {
  return (
    (CARD_HEIGHT.headerRem + CARD_HEIGHT.bodyPaddingRem) * remPx +
    rowIndex * (CARD_HEIGHT.rowPx + CARD_HEIGHT.rowGapRem * remPx)
  );
}

export interface CardPosition {
  left: number;
  top: number;
  width: number;
}

export interface CardLayoutResult {
  positions: CardPosition[];
  height: number;
}

/**
 * Позиции карточек в раскладке «masonry» по измеренным высотам (design
 * review, блокер A). Очередная карточка кладётся в колонку с наименьшим
 * накопленным низом (`columnNextTop`); при равенстве — в колонку с меньшим
 * номером. Довод: `top` следующей карточки не меньше `top` предыдущей —
 * минимум высот колонок не убывает, — поэтому порядок чтения (сверху вниз)
 * совпадает с порядком сортировки экрана, а колонки выравниваются по высоте.
 * Все карточки — прямые дети одного контейнера (а не колонок-`div`, которые
 * пересоздавали бы карточку при переходе между колонками и рвали фокус на
 * живом потоке), эта функция отдаёт только их `left`/`top`/`width`.
 *
 * Пустой список высот — пустые позиции и нулевая высота.
 */
export function layoutCards(
  heights: readonly number[],
  columns: number,
  containerWidth: number,
  gap: number,
): CardLayoutResult {
  const n = Math.max(1, columns);
  const width = (containerWidth - gap * (n - 1)) / n;
  const columnNextTop = Array.from({ length: n }, () => 0);
  const positions = heights.map((height) => {
    let column = 0;
    for (let candidate = 1; candidate < n; candidate += 1) {
      if ((columnNextTop[candidate] ?? 0) < (columnNextTop[column] ?? 0)) {
        column = candidate;
      }
    }
    const top = columnNextTop[column] ?? 0;
    const left = column * (width + gap);
    columnNextTop[column] = top + height + gap;
    return { left, top, width };
  });
  const containerHeight =
    positions.length === 0
      ? 0
      : Math.max(...positions.map((position, index) => position.top + (heights[index] ?? 0)));
  return { positions, height: containerHeight };
}
